import { useMemo, useState } from "react";
import {
  Box,
  Sparkles,
  Loader2,
  Download,
  Upload,
  X,
  AlertTriangle,
  ImageIcon,
  Code2,
  Gamepad2,
  Copy,
  Check,
  SlidersHorizontal,
  RefreshCw,
  ChevronDown,
} from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";
import TaskBanner from "../components/TaskBanner.jsx";
import GenerationProgress from "../components/GenerationProgress.jsx";
import ModelViewer from "../components/ModelViewer.jsx";
import SpeechToTextButton, {
  appendTranscript,
} from "../components/SpeechToTextButton.jsx";
import {
  generateModel3d,
  executeCadQueryCode,
  refineGameModel,
  apiUrl,
} from "../lib/api.js";
import {
  buildCadPrompt,
  CAD_PRESETS,
  CAD_UNITS,
  convertSizeHints,
  unitSuffix,
  EMPTY_SIZE_HINTS,
} from "../lib/cadFeatures.js";
import { usePendingTask, useHomeTaskDone } from "../hooks/usePendingTask.js";
import { useI18n } from "../lib/i18n.jsx";

const MAX_UPLOAD_MB = 8;

const GAME_PRESETS = [
  "Low-poly treasure chest with gold trim, game-ready prop",
  "Stylized sci-fi energy crate with glowing panels",
  "Fantasy sword with ornate crossguard, game prop",
  "Cartoon tree with rounded leaves, mobile game asset",
];

async function fetchReferenceDataUrl(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Could not load reference image.");
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read reference image."));
    reader.readAsDataURL(blob);
  });
}

function NumField({ label, value, onChange, min = 0, step = 1 }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type="number"
        min={min}
        step={step}
        value={value === "" || value == null ? "" : value}
        onChange={(e) =>
          onChange(e.target.value === "" ? "" : parseFloat(e.target.value) || 0)
        }
        className="input"
      />
    </div>
  );
}

export default function Model3DStudio() {
  const { t } = useI18n();
  const [studioMode, setStudioMode] = useState("cad");
  const [gameInput, setGameInput] = useState("text");
  const [gamePrompt, setGamePrompt] = useState(GAME_PRESETS[0]);
  const [cadPrompt, setCadPrompt] = useState(CAD_PRESETS[0]);
  const [sizeHints, setSizeHints] = useState({ ...EMPTY_SIZE_HINTS });
  const [showSizeHints, setShowSizeHints] = useState(false);
  const [refImage, setRefImage] = useState("");
  const [billboardScale, setBillboardScale] = useState(1);
  const [seed, setSeed] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [taskBanner, setTaskBanner] = useState(null);
  const [result, setResult] = useState(null);
  const [modelUrl, setModelUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [cadCode, setCadCode] = useState("");

  const [imagePrompt, setImagePrompt] = useState("");
  const [gameRefImage, setGameRefImage] = useState("");

  const cadPromptFinal = useMemo(
    () => buildCadPrompt(cadPrompt, showSizeHints ? sizeHints : {}),
    [cadPrompt, sizeHints, showSizeHints]
  );

  const setHint = (key, value) => {
    setSizeHints((prev) => ({ ...prev, [key]: value }));
  };

  const applyResult = async (data) => {
    setResult(data);
    const url = data.modelUrl || data.downloadUrl || "";
    setModelUrl(url.startsWith("/") ? apiUrl(url) : url);
    if (data.code) setCadCode(data.code);
    if (data.imagePrompt) setImagePrompt(data.imagePrompt);
    if (data.billboardScale) setBillboardScale(Number(data.billboardScale) || 1);

    if (data.referenceImageUrl) {
      try {
        const refUrl = data.referenceImageUrl.startsWith("/")
          ? apiUrl(data.referenceImageUrl)
          : data.referenceImageUrl;
        setGameRefImage(await fetchReferenceDataUrl(refUrl));
      } catch {
        /* keep prior reference */
      }
    }
  };

  const runJob = async (runner) => {
    setLoading(true);
    setError(null);
    setStatus(t("common.starting", "Starting…"));
    try {
      const data = await runner({ onStatus: setStatus, timeoutMs: 25 * 60 * 1000 });
      await applyResult(data);
    } catch (e) {
      setError(e.message || "3D model generation failed.");
    } finally {
      setLoading(false);
      setStatus("");
    }
  };

  const generateCad = async () => {
    if (loading) return;
    if (!cadPrompt.trim()) {
      setError("Describe the part you want to create.");
      return;
    }
    setResult(null);
    setModelUrl("");
    await runJob((cfg) =>
      generateModel3d({ studioMode: "cad", prompt: cadPromptFinal }, cfg)
    );
  };

  const generateGame = async () => {
    if (loading) return;
    if (gameInput === "text" && !gamePrompt.trim()) {
      setError("Enter a game asset description.");
      return;
    }
    if (gameInput === "image" && !refImage) {
      setError("Upload a reference image first.");
      return;
    }
    setResult(null);
    setModelUrl("");
    await runJob((cfg) =>
      generateModel3d(
        {
          studioMode: "game",
          prompt: gamePrompt,
          image: gameInput === "image" ? refImage : "",
          seed: Number(seed) || 0,
          billboardScale: Number(billboardScale) || 1,
        },
        cfg
      )
    );
  };

  const applyCadChanges = () => generateCad();

  const runEditedCode = async () => {
    if (loading || !cadCode.trim()) {
      setError("No CadQuery code to run.");
      return;
    }
    setResult(null);
    setModelUrl("");
    await runJob((cfg) => executeCadQueryCode(cadCode, cfg));
  };

  const refineGame = async ({ regenerateImage = false } = {}) => {
    if (loading) return;
    const image = gameRefImage || refImage;
    if (!image && !regenerateImage) {
      setError("Generate or upload a reference image first.");
      return;
    }
    if (regenerateImage && !imagePrompt.trim()) {
      setError("Enter an image prompt first.");
      return;
    }
    setResult(null);
    setModelUrl("");
    await runJob((cfg) =>
      refineGameModel(
        {
          referenceImage: image,
          imagePrompt,
          prompt: gamePrompt,
          seed: Number(seed) || 0,
          regenerateImage,
          billboardScale: Number(billboardScale) || 1,
        },
        cfg
      )
    );
  };

  usePendingTask("/model3d", (task) => {
    setTaskBanner(task.rawPrompt || task.prompt);
    const next = String(task.prompt || "");
    if (studioMode === "cad") {
      setCadPrompt(next);
    } else {
      setGamePrompt(next);
    }
    studioMode === "cad" ? generateCad() : generateGame();
  });
  const taskDone = useHomeTaskDone(loading, error, Boolean(taskBanner));

  const downloadExt = result?.format || (studioMode === "cad" ? "glb" : "glb");
  const downloadName = `consensus-model-${Date.now()}.${downloadExt}`;
  const stlDownloadUrl = result?.stlDownloadUrl
    ? result.stlDownloadUrl.startsWith("/")
      ? apiUrl(result.stlDownloadUrl)
      : result.stlDownloadUrl
    : "";
  const showPreview = Boolean(modelUrl);

  const copyCode = async () => {
    if (!cadCode) return;
    try {
      await navigator.clipboard.writeText(cadCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("Could not copy code.");
    }
  };

  const cadUnit = sizeHints.unit || "mm";
  const u = unitSuffix(cadUnit);
  const dimStep = cadUnit === "in" ? 0.01 : cadUnit === "cm" ? 0.1 : 1;

  const subtitle =
    studioMode === "cad"
      ? t(
          "model3d.subtitleCad",
          "Describe any printable part in plain language. AI writes CadQuery and exports GLB (plus STL for printing)."
        )
      : t(
          "model3d.subtitleGame",
          "AI generates a prop image and wraps it in a downloadable GLB for games."
        );

  return (
    <div className="flex flex-col min-h-full">
      <PageHeader icon={Box} title={t("model3d.title", "3D Model Studio")} subtitle={subtitle} />

      {taskBanner && (
        <TaskBanner
          label={t("model3d.title", "3D Model Studio")}
          prompt={taskBanner}
          onDismiss={() => setTaskBanner(null)}
          completed={taskDone}
        />
      )}

      <div className="flex-1 px-6 md:px-10 py-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <section className="card space-y-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStudioMode("cad")}
              className={studioMode === "cad" ? "btn-primary flex-1" : "btn-secondary flex-1"}
            >
              <Code2 className="h-4 w-4" />
              {t("model3d.cadMode", "CAD mode")}
            </button>
            <button
              type="button"
              onClick={() => setStudioMode("game")}
              className={studioMode === "game" ? "btn-primary flex-1" : "btn-secondary flex-1"}
            >
              <Gamepad2 className="h-4 w-4" />
              {t("model3d.gameMode", "Game asset mode")}
            </button>
          </div>

          {studioMode === "cad" ? (
            <>
              <div>
                <label className="label">{t("model3d.cadPrompt", "Describe your part")}</label>
                <div className="relative">
                  <textarea
                    value={cadPrompt}
                    onChange={(e) => setCadPrompt(e.target.value)}
                    rows={5}
                    className="input pr-10"
                    placeholder="Anything you can describe — e.g. a hinged box with a snap lid, 80mm wide, or a custom drone motor mount"
                  />
                  <div className="absolute right-2 top-2">
                    <SpeechToTextButton
                      onTranscript={(text) =>
                        setCadPrompt((prev) => `${prev} ${text}`.trim())
                      }
                      onError={setError}
                    />
                  </div>
                </div>
                <p className="text-[11px] text-white/45 mt-1.5">
                  Be specific about shape, holes, fillets, and sizes. Use mm, cm, or inches in your
                  text — AI converts to millimeters for printing.
                </p>
              </div>

              <div>
                <p className="label">{t("model3d.tryPresets", "Try an example")}</p>
                <div className="flex flex-wrap gap-2">
                  {CAD_PRESETS.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setCadPrompt(item)}
                      className="text-xs px-2.5 py-1.5 rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white/70 text-left"
                    >
                      {item.slice(0, 52)}
                      {item.length > 52 ? "…" : ""}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border border-white/10 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowSizeHints((v) => !v)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-white/[0.03] hover:bg-white/[0.05] text-sm text-white/70"
                >
                  <span className="flex items-center gap-2">
                    <SlidersHorizontal className="h-4 w-4" />
                    Optional size hints
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 transition ${showSizeHints ? "rotate-180" : ""}`}
                  />
                </button>
                {showSizeHints && (
                  <div className="p-3 space-y-3 border-t border-white/10">
                    <div className="flex items-center justify-end gap-2">
                      <label className="text-xs text-white/50">Units</label>
                      <select
                        value={cadUnit}
                        onChange={(e) =>
                          setSizeHints((prev) => convertSizeHints(prev, e.target.value))
                        }
                        className="input text-sm py-1.5 w-auto"
                      >
                        {CAD_UNITS.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <NumField
                        label={`Length (${u})`}
                        value={sizeHints.length}
                        onChange={(v) => setHint("length", v)}
                        step={dimStep}
                      />
                      <NumField
                        label={`Width (${u})`}
                        value={sizeHints.width}
                        onChange={(v) => setHint("width", v)}
                        step={dimStep}
                      />
                      <NumField
                        label={`Height (${u})`}
                        value={sizeHints.height}
                        onChange={(v) => setHint("height", v)}
                        step={dimStep}
                      />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <NumField
                        label={`Wall thickness (${u})`}
                        value={sizeHints.wallThickness}
                        onChange={(v) => setHint("wallThickness", v)}
                        step={cadUnit === "in" ? 0.01 : 0.5}
                      />
                      <NumField
                        label={`Corner fillet (${u})`}
                        value={sizeHints.fillet}
                        onChange={(v) => setHint("fillet", v)}
                        step={cadUnit === "in" ? 0.01 : 0.5}
                      />
                    </div>
                    <p className="text-[11px] text-white/45">
                      Hints are added to your description — you can still describe everything in
                      the prompt above.
                    </p>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={generateCad}
                disabled={loading}
                className="btn-primary w-full justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating GLB…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate CAD GLB
                  </>
                )}
              </button>

              {result && (
                <button
                  type="button"
                  onClick={applyCadChanges}
                  disabled={loading}
                  className="btn-secondary w-full justify-center"
                >
                  <RefreshCw className="h-4 w-4" />
                  Regenerate with updated prompt
                </button>
              )}

              <div className="border border-white/10 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowCode((v) => !v)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-white/[0.03] hover:bg-white/[0.05] text-sm text-white/70"
                >
                  <span>Advanced: view CadQuery code</span>
                  <ChevronDown className={`h-4 w-4 transition ${showCode ? "rotate-180" : ""}`} />
                </button>
                {showCode && (
                  <div className="p-3 space-y-2 border-t border-white/10">
                    {cadCode ? (
                      <>
                        <div className="flex justify-end">
                          <button type="button" onClick={copyCode} className="btn-secondary text-xs">
                            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                            {copied ? "Copied" : "Copy"}
                          </button>
                        </div>
                        <pre className="max-h-48 overflow-auto rounded-lg border border-white/10 bg-black/40 p-3 text-[11px] text-emerald-100/90">
                          {cadCode}
                        </pre>
                        <button
                          type="button"
                          onClick={runEditedCode}
                          disabled={loading}
                          className="btn-secondary w-full justify-center text-xs"
                        >
                          Run edited code
                        </button>
                      </>
                    ) : (
                      <p className="text-xs text-white/45">Generate a model first to see the code.</p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setGameInput("text")}
                  className={gameInput === "text" ? "btn-secondary flex-1" : "btn-secondary flex-1 opacity-70"}
                >
                  <Sparkles className="h-4 w-4" />
                  Text → GLB
                </button>
                <button
                  type="button"
                  onClick={() => setGameInput("image")}
                  className={gameInput === "image" ? "btn-secondary flex-1" : "btn-secondary flex-1 opacity-70"}
                >
                  <ImageIcon className="h-4 w-4" />
                  Image → GLB
                </button>
              </div>

              {gameInput === "text" ? (
                <div>
                  <label className="label">{t("model3d.gamePrompt", "Game asset description")}</label>
                  <div className="relative">
                    <textarea
                      value={gamePrompt}
                      onChange={(e) => setGamePrompt(e.target.value)}
                      rows={3}
                      className="input pr-10"
                    />
                    <div className="absolute right-2 top-2">
                      <SpeechToTextButton
                        onTranscript={(text) => setGamePrompt((prev) => `${prev} ${text}`.trim())}
                        onError={setError}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {GAME_PRESETS.map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setGamePrompt(item)}
                        className="text-xs px-2 py-1 rounded border border-white/10 text-white/60 hover:text-white/80"
                      >
                        {item.slice(0, 36)}…
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="label">{t("model3d.referenceImage", "Reference image")}</label>
                  {refImage ? (
                    <div className="relative rounded-lg overflow-hidden border border-white/10">
                      <img src={refImage} alt="Reference" className="w-full max-h-48 object-cover" />
                      <button
                        type="button"
                        onClick={() => {
                          setRefImage("");
                          setGameRefImage("");
                        }}
                        className="absolute top-2 right-2 p-1.5 rounded-md bg-black/60 text-white/80"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center gap-2 border border-dashed border-white/15 rounded-lg p-6 cursor-pointer hover:bg-white/[0.03]">
                      <Upload className="h-5 w-5 text-white/50" />
                      <span className="text-sm text-white/60">Upload PNG or JPG</span>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = () => {
                            const dataUrl = String(reader.result || "");
                            setRefImage(dataUrl);
                            setGameRefImage(dataUrl);
                          };
                          reader.readAsDataURL(file);
                        }}
                      />
                    </label>
                  )}
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label">Prop size ({billboardScale.toFixed(1)}×)</label>
                  <input
                    type="range"
                    min={0.5}
                    max={3}
                    step={0.1}
                    value={billboardScale}
                    onChange={(e) => setBillboardScale(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                <NumField
                  label="Image seed (0 = random)"
                  value={seed}
                  onChange={setSeed}
                  min={0}
                />
              </div>

              <button
                type="button"
                onClick={generateGame}
                disabled={loading}
                className="btn-primary w-full justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating GLB…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate game GLB
                  </>
                )}
              </button>

              {result && (
                <div className="rounded-lg border border-white/10 p-3 space-y-3">
                  <p className="text-sm font-medium text-white/80">Adjust game asset</p>
                  <div>
                    <label className="label">Image prompt</label>
                    <textarea
                      value={imagePrompt}
                      onChange={(e) => setImagePrompt(e.target.value)}
                      rows={2}
                      className="input text-sm"
                    />
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => refineGame({ regenerateImage: false })}
                      disabled={loading}
                      className="btn-secondary justify-center text-xs"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Rebuild GLB
                    </button>
                    <button
                      type="button"
                      onClick={() => refineGame({ regenerateImage: true })}
                      disabled={loading}
                      className="btn-secondary justify-center text-xs"
                    >
                      <ImageIcon className="h-3.5 w-3.5" />
                      New image + GLB
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {loading && status && <GenerationProgress message={status} />}
          {error && (
            <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 flex gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <p className="text-[11px] text-white/45">
            {studioMode === "cad"
              ? "Describe any part in the prompt — no templates required. CAD exports GLB for preview/games and STL for 3D printing."
              : "Game mode builds a real 3D GLB — NVIDIA TRELLIS mesh when available, otherwise a volumetric relief from your image."}
          </p>
        </section>

        <section className="card space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-white/85">{t("model3d.preview", "Preview")}</h2>
            {modelUrl && (
              <div className="flex flex-wrap items-center gap-2">
                <a href={modelUrl} download={downloadName} className="btn-secondary text-xs">
                  <Download className="h-3.5 w-3.5" />
                  Download .{downloadExt}
                </a>
                {studioMode === "cad" && stlDownloadUrl && (
                  <a
                    href={stlDownloadUrl}
                    download={`consensus-model-${Date.now()}.stl`}
                    className="btn-secondary text-xs"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download STL
                  </a>
                )}
              </div>
            )}
          </div>

          {showPreview ? (
            <ModelViewer src={modelUrl} format={downloadExt} />
          ) : (
            <ModelViewer src="" />
          )}

          {result && (
            <div className="text-xs text-white/55 space-y-1 border-t border-white/10 pt-3">
              <p>
                <span className="text-white/35">Mode:</span> {result.studioMode || studioMode}
              </p>
              <p>
                <span className="text-white/35">Format:</span> {downloadExt.toUpperCase()}
              </p>
              {studioMode === "cad" && (result?.prompt || cadPromptFinal) && (
                <p>
                  <span className="text-white/35">Prompt:</span>{" "}
                  {result?.prompt || cadPromptFinal}
                </p>
              )}
              {studioMode === "game" && result?.meshSource && (
                <p>
                  <span className="text-white/35">3D method:</span>{" "}
                  {result.meshSource === "trellis-image"
                    ? "TRELLIS from your reference image"
                    : result.meshSource === "trellis-text"
                      ? "TRELLIS from your text description"
                      : "Simplified relief from image (lower quality)"}
                </p>
              )}
              {studioMode === "game" && result?.trellisPrompt && (
                <p>
                  <span className="text-white/35">3D prompt:</span> {result.trellisPrompt}
                </p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
